import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import Papa from 'papaparse'
import { allDeviceTypesStore } from "../stores/device_types/all_device_types_store"
import { allDeploymentStatusesStore } from "../stores/deployment_statuses/all_deployment_statuses_store"
import { allRoomsStore } from "../stores/locales/all_rooms_store"

// Connects to data-controller="csv-parser"
export default class extends Controller {
    static stores = [
        allDeviceTypesStore,
        allDeploymentStatusesStore,
        allRoomsStore
    ]

    static values = {
        maxFileSize: { type: Number, default: 5242880 },  // 5MB
        maxRows: { type: Number, default: 10000 },
        requiredHeaders: { type: Array, default: ["name", "device_type", "manufacturer", "model", "deployment_status", "room"] },
        optionalHeaders: { type: Array, default: ["dns", "ip_address", "mac_address", "serial_number"] }
    }

    connect() {
        useStore(this)
    }

    /**
     * Main entry point - parses and validates a CSV file
     * @param {File} file - The uploaded CSV file
     * @returns {Promise<{success: boolean, devices?: Array, errors?: Array}>}
     */
    async parseFile(file) {
        // 1. Validate file type
        const typeError = this.validateFileType(file)
        if (typeError) return { success: false, errors: [typeError] }

        // 2. Validate file size
        const sizeError = this.validateFileSize(file)
        if (sizeError) return { success: false, errors: [sizeError] }

        // 3. Read file content
        const content = await this.readFileContent(file)

        // 4. Parse CSV content with PapaParse
        const parseResult = this.parseContent(content)

        // 5. Check for PapaParse errors
        if (parseResult.errors && parseResult.errors.length > 0) {
            return { success: false, errors: [{ message: "File is not a valid CSV format" }] }
        }

        // 6. Validate headers
        const headers = parseResult.meta.fields || []
        const headerError = this.validateHeaders(headers)
        if (headerError) return { success: false, errors: [headerError] }

        // 7. Check for empty file (no data rows)
        if (!parseResult.data || parseResult.data.length === 0) {
            return { success: false, errors: [{ message: "File contains no data rows" }] }
        }

        // 8. Check max row limit
        if (parseResult.data.length > this.maxRowsValue) {
            return { success: false, errors: [{ message: `File exceeds the maximum of ${this.maxRowsValue.toLocaleString()} rows` }] }
        }

        // 9. Build header mapping (normalized field name -> actual CSV header key)
        const headerMap = this.buildHeaderMap(parseResult.meta.fields || [])

        // 10. Validate row data against stores
        return await this.validateRows(parseResult.data, headerMap)
    }

    /**
     * Reads file content as text using FileReader wrapped in a Promise
     * @param {File} file
     * @returns {Promise<string>}
     */
    readFileContent(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (event) => resolve(event.target.result)
            reader.onerror = () => reject(new Error("Failed to read file"))
            reader.readAsText(file)
        })
    }

    /**
     * Parses CSV content using PapaParse
     * @param {string} content - Raw CSV text
     * @returns {object} PapaParse result with data, errors, and meta
     */
    parseContent(content) {
        return Papa.parse(content, {
            header: true,
            skipEmptyLines: true
        })
    }

    /**
     * Validates that all required headers are present (case-insensitive)
     * @param {string[]} headers - Array of header names from parsed CSV
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateHeaders(headers) {
        const normalizedHeaders = headers.map(h => h.toLowerCase().trim())
        const missingHeaders = this.requiredHeadersValue.filter(
            required => !normalizedHeaders.includes(required.toLowerCase())
        )

        if (missingHeaders.length > 0) {
            return { message: `Missing required columns: ${missingHeaders.join(', ')}` }
        }

        return null
    }

    /**
     * Validates that the file has a .csv extension (case-insensitive)
     * @param {File} file
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateFileType(file) {
        if (!file || !file.name) {
            return { message: "No file provided" }
        }

        const name = file.name
        const lastDotIndex = name.lastIndexOf('.')

        // No extension at all, or name ends with a dot (empty extension)
        if (lastDotIndex === -1 || lastDotIndex === name.length - 1) {
            return { message: "Only .csv files are accepted" }
        }

        const extension = name.substring(lastDotIndex + 1)
        if (extension.toLowerCase() !== 'csv') {
            return { message: "Only .csv files are accepted" }
        }

        return null
    }

    /**
     * Validates that the file does not exceed maxFileSize
     * @param {File} file
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateFileSize(file) {
        if (file.size > this.maxFileSizeValue) {
            return { message: "File exceeds the 5MB size limit" }
        }
        return null
    }

    /**
     * Builds a mapping from normalized field names to actual CSV header keys.
     * PapaParse uses the actual header text as object keys, so we need this
     * to handle case-insensitive header matching when extracting row values.
     * @param {string[]} headers - The actual header names from the CSV
     * @returns {object} Map of normalized field name -> actual header key
     */
    buildHeaderMap(headers) {
        const allFields = [...this.requiredHeadersValue, ...this.optionalHeadersValue]
        const headerMap = {}

        for (const field of allFields) {
            const match = headers.find(h => h.toLowerCase().trim() === field.toLowerCase())
            if (match) {
                headerMap[field] = match
            }
        }

        return headerMap
    }

    /**
     * Validates all rows against organization stores and returns resolved device records or errors.
     * Collects ALL errors across all rows (not just the first), capped at 100 displayed errors.
     * Resolves manufacturer and model names to UUIDs via API calls (with caching).
     * @param {object[]} rows - Parsed CSV row objects from PapaParse
     * @param {object} headerMap - Mapping from normalized field names to actual header keys
     * @returns {Promise<{success: boolean, devices?: Array, errors?: Array}>}
     */
    async validateRows(rows, headerMap) {
        const errors = []
        const devices = []
        const maxDisplayedErrors = 100

        // Caches for manufacturer and model lookups to avoid redundant API calls
        const manufacturerCache = {} // keyed by device_type_id
        const modelCache = {}        // keyed by device_type_id + '::' + manufacturer_id

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            const rowErrors = []

            // Extract values using the header map
            const nameValue = this.getRowValue(row, 'name', headerMap)
            const deviceTypeValue = this.getRowValue(row, 'device_type', headerMap)
            const manufacturerValue = this.getRowValue(row, 'manufacturer', headerMap)
            const modelValue = this.getRowValue(row, 'model', headerMap)
            const deploymentStatusValue = this.getRowValue(row, 'deployment_status', headerMap)
            const roomValue = this.getRowValue(row, 'room', headerMap)

            // Validate required fields
            const nameError = this.validateName(nameValue, i)
            if (nameError) rowErrors.push(nameError)

            const typeError = this.validateDeviceType(deviceTypeValue, i)
            if (typeError) rowErrors.push(typeError)

            const statusError = this.validateDeploymentStatus(deploymentStatusValue, i)
            if (statusError) rowErrors.push(statusError)

            const roomError = this.validateRoom(roomValue, i)
            if (roomError) rowErrors.push(roomError)

            // Validate manufacturer (must not be blank)
            const manufacturerBlankError = this.validateRequiredField(manufacturerValue, 'manufacturer', i)
            if (manufacturerBlankError) rowErrors.push(manufacturerBlankError)

            // Validate model (must not be blank)
            const modelBlankError = this.validateRequiredField(modelValue, 'model', i)
            if (modelBlankError) rowErrors.push(modelBlankError)

            // If basic field validations failed, skip API resolution for this row
            if (rowErrors.length > 0) {
                errors.push(...rowErrors)
                continue
            }

            // All basic validations passed — resolve IDs
            const matchedType = this.findDeviceType(deviceTypeValue)
            const matchedStatus = this.findDeploymentStatus(deploymentStatusValue)
            const matchedRoom = this.findRoom(roomValue)

            // Resolve manufacturer via API (with caching)
            const matchedManufacturer = await this.resolveManufacturer(
                matchedType.id, manufacturerValue, manufacturerCache, i
            )
            if (matchedManufacturer.error) {
                errors.push(matchedManufacturer.error)
                continue
            }

            // Resolve model via API (with caching)
            const matchedModel = await this.resolveModel(
                matchedType.id, matchedManufacturer.id, modelValue, modelCache, i
            )
            if (matchedModel.error) {
                errors.push(matchedModel.error)
                continue
            }

            devices.push({
                index: i,
                name: nameValue,
                device_type_id: matchedType.id,
                device_type_name: matchedType.name,
                device_type_icon: matchedType.icon,
                manufacturer_id: matchedManufacturer.id,
                manufacturer_name: matchedManufacturer.name,
                model_id: matchedModel.id,
                model_name: matchedModel.name,
                deployment_status_id: matchedStatus.id,
                deployment_status_name: matchedStatus.name,
                deployment_status_color: matchedStatus.status_color,
                room_id: matchedRoom.id,
                room_name: matchedRoom.name,
                dns: this.getRowValue(row, 'dns', headerMap) || '',
                ip_address: this.getRowValue(row, 'ip_address', headerMap) || '',
                mac_address: this.getRowValue(row, 'mac_address', headerMap) || '',
                serial_number: this.getRowValue(row, 'serial_number', headerMap) || '',
                placed: false
            })
        }

        if (errors.length > 0) {
            return { success: false, errors: errors.slice(0, maxDisplayedErrors) }
        }

        return { success: true, devices }
    }

    /**
     * Extracts a value from a parsed row using the header map.
     * @param {object} row - A parsed CSV row object
     * @param {string} field - The normalized field name
     * @param {object} headerMap - Mapping from normalized field names to actual header keys
     * @returns {string} The field value, or empty string if not found
     */
    getRowValue(row, field, headerMap) {
        const actualKey = headerMap[field]
        if (!actualKey) return ''
        return row[actualKey] != null ? String(row[actualKey]) : ''
    }

    /**
     * Validates the name field: must not be blank and must not exceed 255 characters.
     * @param {string} value - The name field value
     * @param {number} rowIndex - Zero-based row index
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateName(value, rowIndex) {
        const rowNum = rowIndex + 1

        if (!value || value.trim() === '') {
            return { row: rowNum, field: 'name', value: value || '', message: `Row ${rowNum}: name cannot be blank` }
        }

        if (value.length > 255) {
            return { row: rowNum, field: 'name', value: value, message: `Row ${rowNum}: name exceeds 255 characters` }
        }

        return null
    }

    /**
     * Validates the device_type field: must not be blank and must match an existing
     * device type name (case-insensitive) in allDeviceTypesStore.
     * @param {string} value - The device_type field value
     * @param {number} rowIndex - Zero-based row index
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateDeviceType(value, rowIndex) {
        const rowNum = rowIndex + 1

        if (!value || value.trim() === '') {
            return { row: rowNum, field: 'device_type', value: value || '', message: `Row ${rowNum}: device_type cannot be blank` }
        }

        const match = this.findDeviceType(value)
        if (!match) {
            return { row: rowNum, field: 'device_type', value: value, message: `Row ${rowNum}: device_type '${value}' not found` }
        }

        return null
    }

    /**
     * Validates the deployment_status field: must not be blank and must match an existing
     * deployment status name (case-insensitive) in allDeploymentStatusesStore.
     * @param {string} value - The deployment_status field value
     * @param {number} rowIndex - Zero-based row index
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateDeploymentStatus(value, rowIndex) {
        const rowNum = rowIndex + 1

        if (!value || value.trim() === '') {
            return { row: rowNum, field: 'deployment_status', value: value || '', message: `Row ${rowNum}: deployment_status cannot be blank` }
        }

        const match = this.findDeploymentStatus(value)
        if (!match) {
            return { row: rowNum, field: 'deployment_status', value: value, message: `Row ${rowNum}: deployment_status '${value}' not found` }
        }

        return null
    }

    /**
     * Validates the room field: must not be blank and must match an existing
     * room name in allRoomsStore.
     * @param {string} value - The room field value
     * @param {number} rowIndex - Zero-based row index
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateRoom(value, rowIndex) {
        const rowNum = rowIndex + 1

        if (!value || value.trim() === '') {
            return { row: rowNum, field: 'room', value: value || '', message: `Row ${rowNum}: room cannot be blank` }
        }

        const match = this.findRoom(value)
        if (!match) {
            return { row: rowNum, field: 'room', value: value, message: `Row ${rowNum}: room '${value}' not found` }
        }

        return null
    }

    /**
     * Finds a device type by name (case-insensitive) in the store.
     * @param {string} value - The device type name to search for
     * @returns {object|null} The matching device type object or null
     */
    findDeviceType(value) {
        if (!value) return null
        const normalized = value.toLowerCase().trim()
        return this.allDeviceTypesStoreValue.find(
            dt => dt.name && dt.name.toLowerCase().trim() === normalized
        ) || null
    }

    /**
     * Finds a deployment status by name (case-insensitive) in the store.
     * @param {string} value - The deployment status name to search for
     * @returns {object|null} The matching deployment status object or null
     */
    findDeploymentStatus(value) {
        if (!value) return null
        const normalized = value.toLowerCase().trim()
        return this.allDeploymentStatusesStoreValue.find(
            ds => ds.name && ds.name.toLowerCase().trim() === normalized
        ) || null
    }

    /**
     * Finds a room by name in the store.
     * @param {string} value - The room name to search for
     * @returns {object|null} The matching room object or null
     */
    findRoom(value) {
        if (!value) return null
        const normalized = value.toLowerCase().trim()
        return this.allRoomsStoreValue.find(
            r => r.name && r.name.toLowerCase().trim() === normalized
        ) || null
    }

    /**
     * Validates that a required field is not blank.
     * @param {string} value - The field value
     * @param {string} fieldName - The field name for error messages
     * @param {number} rowIndex - Zero-based row index
     * @returns {object|null} Error object if invalid, null if valid
     */
    validateRequiredField(value, fieldName, rowIndex) {
        const rowNum = rowIndex + 1
        if (!value || value.trim() === '') {
            return { row: rowNum, field: fieldName, value: value || '', message: `Row ${rowNum}: ${fieldName} cannot be blank` }
        }
        return null
    }

    /**
     * Resolves a manufacturer name to its UUID by fetching from the API.
     * Uses a cache keyed by device_type_id to avoid redundant requests.
     * @param {string} deviceTypeId - The device type UUID
     * @param {string} manufacturerName - The manufacturer name from CSV
     * @param {object} cache - Cache object keyed by device_type_id
     * @param {number} rowIndex - Zero-based row index
     * @returns {Promise<{id: string, name: string}|{error: object}>}
     */
    async resolveManufacturer(deviceTypeId, manufacturerName, cache, rowIndex) {
        const rowNum = rowIndex + 1

        // Fetch manufacturers for this device type (cached)
        if (!cache[deviceTypeId]) {
            try {
                cache[deviceTypeId] = await this.fetchManufacturers(deviceTypeId)
            } catch (error) {
                console.error(`[csv-parser] Error fetching manufacturers for device type ${deviceTypeId}:`, error)
                return { error: { row: rowNum, field: 'manufacturer', value: manufacturerName, message: `Row ${rowNum}: failed to fetch manufacturers` } }
            }
        }

        const manufacturers = cache[deviceTypeId]
        const normalized = manufacturerName.toLowerCase().trim()
        const match = manufacturers.find(m => m.name && m.name.toLowerCase().trim() === normalized)

        if (!match) {
            return { error: { row: rowNum, field: 'manufacturer', value: manufacturerName, message: `Row ${rowNum}: manufacturer '${manufacturerName}' not found for this device type` } }
        }

        return match
    }

    /**
     * Resolves a model name to its UUID by fetching from the API.
     * Uses a cache keyed by device_type_id + '::' + manufacturer_id to avoid redundant requests.
     * @param {string} deviceTypeId - The device type UUID
     * @param {string} manufacturerId - The manufacturer UUID
     * @param {string} modelName - The model name from CSV
     * @param {object} cache - Cache object keyed by composite key
     * @param {number} rowIndex - Zero-based row index
     * @returns {Promise<{id: string, name: string}|{error: object}>}
     */
    async resolveModel(deviceTypeId, manufacturerId, modelName, cache, rowIndex) {
        const rowNum = rowIndex + 1
        const cacheKey = `${deviceTypeId}::${manufacturerId}`

        // Fetch models for this device type + manufacturer (cached)
        if (!cache[cacheKey]) {
            try {
                cache[cacheKey] = await this.fetchModels(deviceTypeId, manufacturerId)
            } catch (error) {
                console.error(`[csv-parser] Error fetching models for device type ${deviceTypeId}, manufacturer ${manufacturerId}:`, error)
                return { error: { row: rowNum, field: 'model', value: modelName, message: `Row ${rowNum}: failed to fetch models` } }
            }
        }

        const models = cache[cacheKey]
        const normalized = modelName.toLowerCase().trim()
        const match = models.find(m => m.name && m.name.toLowerCase().trim() === normalized)

        if (!match) {
            return { error: { row: rowNum, field: 'model', value: modelName, message: `Row ${rowNum}: model '${modelName}' not found for this device type and manufacturer` } }
        }

        return match
    }

    /**
     * Fetches all manufacturers for a device type, handling pagination.
     * @param {string} deviceTypeId - The device type UUID
     * @returns {Promise<Array<{id: string, name: string}>>}
     */
    async fetchManufacturers(deviceTypeId) {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({ device_type_id: deviceTypeId })
            if (paginationToken) params.append('pagination_token', paginationToken)

            const response = await fetch(`/api/device_types/device_manufacturer/list?${params}`)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const data = await response.json()
            if (data.manufacturers) {
                results.push(...data.manufacturers.map(m => ({ id: m.id, name: m.name })))
            }
            paginationToken = data.pagination_token
        } while (paginationToken)

        return results
    }

    /**
     * Fetches all models for a device type + manufacturer combination, handling pagination.
     * @param {string} deviceTypeId - The device type UUID
     * @param {string} manufacturerId - The manufacturer UUID
     * @returns {Promise<Array<{id: string, name: string}>>}
     */
    async fetchModels(deviceTypeId, manufacturerId) {
        const results = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({ device_type_id: deviceTypeId, manufacturer_id: manufacturerId })
            if (paginationToken) params.append('pagination_token', paginationToken)

            const response = await fetch(`/api/device_types/device_model/list?${params}`)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const data = await response.json()
            if (data.models) {
                results.push(...data.models.map(m => ({ id: m.id, name: m.name })))
            }
            paginationToken = data.pagination_token
        } while (paginationToken)

        return results
    }
}
